import React, { useState, useMemo, useEffect } from 'react';
import { COLORS } from '../../../utils/colors';
import {
  QuickbooksInvoice,
  roundTo,
  formatMoney,
  capitalizeWordsInSentence,
  LienApiPostBody,
  DillPaymentMethod,
  GroupedLienAutoRelease,
  Job,
  LienTemplate,
  ReleaseFieldData,
  ReleaseTemplateField,
  calculateStripePartialFacilitationFeesAndAmount,
  appDelay,
  getOrdinalSuffix,
  InvoicePaymentDetails,
  Buyer,
  Supplier,
  DocumentsSummary,
  getDiscountInfo,
  getCreditDiscountInfo,
  getBuyerFiservFeeRate,
  getEffectiveCreditCardFeeWaiverConditions,
  hasCurrentDueWaiverCutoff,
} from '@dill/dill-shared';
import { AppCreateRealease } from '../AppCreateRealease/AppCreateRealease';
import { DillButton } from '../Dill/DillButton';
import { DillCheckbox } from '../Dill/DillCheckbox';
import moment from 'moment';
import emptyListImg from '../../../assets/images/emptyList.png';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { Plus, Loader, X, AlertOctagon } from 'react-feather';
import {
  Checks,
  Bank,
  CaretLeft,
  Clock,
  CreditCard,
  LightbulbFilament,
  Warning,
  Trash,
  CaretDown,
  CaretUp,
} from 'phosphor-react';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import { AppDropDown } from '../AppDropDown/AppDropDown';
import { AppRadioButton } from '../AppRadioButton/AppRadioButton';
import './AppPaymentInvoiceTable.scss';
import { twMerge } from 'tailwind-merge';
import { getJobs, updateJob } from '../../../redux/services/jobsService';
import { updateSubSupplier } from '../../../redux/services/subSuppliersService';
import AppAddPaymentMethodModal from '../AppAddPaymentMethodModal/AppAddPaymentMethodModal';
import {
  deletePaymentMethodByMainBuyer,
  deleteSupplierCustomerPaymentMethod,
  dwollaTransactionLimitNotifyDill,
  getMainBuyerPaymentMethods,
  getSupplierCustomerPaymentMethods,
} from '../../../redux/services/paymentsService';
import AppVerifyDwollaMicroTransactionsModal from '../AppVerifyDwollaMicroTransactionsModal/AppVerifyDwollaMicroTransactionsModal';
import { useParamMainBuyerId } from '../../../utils/hooks/useParamMainBuyerId';
import { closeMessageModal, openMessageModal } from '../../../redux/globalSlices/genericSlice';
import { AppInputField } from '../AppInputField/AppInputField';
import AppFilePicker from '../AppFilePicker/AppFilePicker';
import AppRangeSlider from '../AppAppRangeSlider/AppRangeSlider';
import { useWindowActualWidth } from '../../../utils/domTools';
import { APP_ENVIRONMENT } from '../../../utils/constants';
import AppDatePicker from '../AppDatePicker/AppDatePicker';

export const AppPaymentInvoiceTable = ({
  handleClose,
  editedInvoices,
  selectedBuyerId,
  paymentTo,
  paymentFrom,
  onChangeModalType,
  modalType,
  onReleaseDetails,
  onPaymentDetail,
  onChangeInput,
  onBlurInput,
  totalPay,
  onToggleIsPaymentMethod,
  isSelectPaymentMethod,
  sendRealese,
  onAutoGenerateRelease,
  fees,
  waivedFees,
  paymentMethods,
  selectedPaymentMethod,
  onSelectPaymentMethod,
  totalCreditsToUse,
  creditAmount,
  onSetCreditToUse,
  isUseCredits,
  onUseCredits,
  selectedRelease,
  isPartialPayment,
  groupedInvoiceReleases,
  partialReason,
  onAutoAdjustInvoicesToFitLimit,
  onDeleteEditedInvoice,
  onPartialPaymentDetailsChange,
  supplierChargeDetails,
  onSupplierChargeWhoPaysFee,
  onSupplierChargeBuyerTakeRate,
  supplierChargeBuyerTakeRate,
  isAllCurrentDue,
  isAllWithinDiscountDate,
  isWithinWaiverDayOfMonth,
  canSchedulePayment,
  scheduleUnavailableReason,
  paymentTiming,
  onPaymentTimingChange,
  scheduledDate,
  onScheduledDateChange,
  earliestScheduledDate,
  scheduleErrorMessage,
}: {
  handleClose: () => void;
  editedInvoices: {
    editedPayment?: number;
    formattedEditedPaymentValue?: string;
    invoiceOriginal: QuickbooksInvoice;
    discountAmount?: number;
  }[];
  selectedBuyerId: string | undefined;
  paymentTo: string | undefined;
  paymentFrom: string | undefined;
  modalType: string;
  onChangeModalType: (value: string) => void;
  onDeleteEditedInvoice: (value: number) => void;
  onAutoAdjustInvoicesToFitLimit: () => void;
  onReleaseDetails: (
    details: LienApiPostBody[],
    data: {
      [x: string]: {
        job: Job;
        originalJob: Job;
        template: LienTemplate | null;
        currentPdfUrl: string;
        originalPdfUrl: string;
        subSupplierPosition: number | null;
        amount: number;
        formFields: { [x: string]: ReleaseFieldData };
        templateFields: ReleaseTemplateField[] | null;
      };
    }
  ) => void;
  onPaymentDetail: (
    credit: number,
    paymentMethodId: string,
    paymentReason: string,
    editedInvoices: {
      editedPayment?: number;
      formattedEditedPaymentValue?: string;
      invoiceOriginal: QuickbooksInvoice;
      discountAmount?: number;
    }[],
    creditDiscountUsed: { creditId: string; creditAmount: number }[]
  ) => void;
  onChangeInput: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlurInput: (event: React.FocusEvent<HTMLInputElement>) => void;
  totalPay: number;
  onToggleIsPaymentMethod: (value: boolean) => void;
  isSelectPaymentMethod: boolean;
  sendRealese: boolean;
  fees: number;
  waivedFees?: number;
  onAutoGenerateRelease: () => void;
  paymentMethods: DillPaymentMethod[];
  selectedPaymentMethod: string;
  onSelectPaymentMethod: (val: string) => void;
  totalCreditsToUse: number;
  creditAmount: number;
  onSetCreditToUse: (val: number) => void;
  isUseCredits: boolean;
  onUseCredits: (value: boolean) => void;
  selectedRelease: {
    [x: string]: {
      job: Job;
      originalJob: Job;
      template: LienTemplate | null;
      currentPdfUrl: string;
      originalPdfUrl: string;
      subSupplierPosition: number | null;
      amount: number;
      formFields: { [x: string]: ReleaseFieldData };
      templateFields: ReleaseTemplateField[] | null;
    };
  };
  isPartialPayment: boolean;
  groupedInvoiceReleases: GroupedLienAutoRelease[];
  partialReason: string;
  onPartialPaymentDetailsChange: ({
    notes,
    urls,
    files,
  }: {
    notes?: string;
    urls?: {
      name: string;
      url: string;
    }[];
    files?: File[];
  }) => void;
  onSupplierChargeWhoPaysFee: (value: 'SUPPLIER' | 'BUYER' | 'BOTH') => void;
  onSupplierChargeBuyerTakeRate: (value: number) => void;
  supplierChargeDetails?: {
    customerDetails: {
      buyer: Buyer;
      usersEmailsDetails: {
        email: string;
        userId: string;
        isInvitedUser: boolean;
        isStatementsEmailOptOut: boolean;
        isPaymentRemindersEmailOptOut: boolean;
      }[];
      documentsSummary: DocumentsSummary;
    };
    supplier: Supplier;
  };
  supplierChargeBuyerTakeRate: number;
  isAllCurrentDue: boolean;
  isAllWithinDiscountDate: boolean;
  /** Whether the pay date falls on or before the supplier's prox-terms cutoff day. */
  isWithinWaiverDayOfMonth: boolean;
  /**
   * Pay-now vs. schedule-for-later. The choice is made here, alongside the total, but the state and
   * the submit both live in AppSelectedPayInvoicesModal — this component only renders the control.
   */
  canSchedulePayment: boolean;
  /**
   * Shown in place of the control when scheduling is off for a reason the buyer should know about
   * (today: they are sending a lien release). Empty when there is nothing to explain — scheduling
   * simply isn't part of that flow.
   */
  scheduleUnavailableReason: string;
  paymentTiming: 'NOW' | 'SCHEDULED';
  onPaymentTimingChange: (value: 'NOW' | 'SCHEDULED') => void;
  scheduledDate: Date | null;
  onScheduledDateChange: (date: Date | null) => void;
  earliestScheduledDate: Date;
  scheduleErrorMessage: string;
}) => {
  const dispatch = useAppDispatch();
  const width = useWindowActualWidth();
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { selectedMainBuyer } = useAppSelector((state) => state.buyers);
  const [invoiceListErrorMessage, setInvoiceListErrorMessage] = useState('');
  const [isAutoAdjustInvoicesToFitLimit, setIsAutoAdjustInvoicesToFitLimit] = useState(false);
  const [releaseData, setReleaseData] = useState<LienApiPostBody[]>([]);
  const [partialPaymentReason, setPartialPaymentReason] = useState('');
  const [partialPaymentNotes, setPartialPaymentNotes] = useState('');
  const [partialPaymentAttachmentsUrls, setPartialPaymentAttachmentsUrls] = useState<
    {
      name: string;
      url: string;
    }[]
  >([]);
  const [partialPaymentAttachmentsFiles, setPartialPaymentAttachmentsFiles] = useState<File[]>([]);
  const [releaseErrors, setReleaseErrors] = useState<{
    [key: string]: { [key: string]: string };
  }>({});
  const [jobOwner, setJobOwner] = useState('');
  const [updateJobOwner, setUpdateJobOwner] = useState(false);
  const [selectedMainBuyerId] = useParamMainBuyerId();
  const [isAppAddPaymentMethodModalOpen, setIsAppAddPaymentMethodModalOpen] = useState(false);
  const [
    isAppVerifyDwollaMicroTransactionsModalOpen,
    setIsAppVerifyDwollaMicroTransactionsModalOpen,
  ] = useState(false);
  const [
    selectedPaymentMethodToVerifyMicroTransactions,
    setSelectedPaymentMethodToVerifyMicroTransactions,
  ] = useState<DillPaymentMethod>();
  const [releaseGroupDetails, setReleaseGroupDetails] = useState<{
    [x: string]: {
      job: Job;
      originalJob: Job;
      template: LienTemplate | null;
      currentPdfUrl: string;
      originalPdfUrl: string;
      subSupplierPosition: number | null;
      amount: number;
      formFields: { [x: string]: ReleaseFieldData };
      templateFields: ReleaseTemplateField[] | null;
    };
  }>({});
  const [vendorDetail, setVendorDetail] = useState<{
    [x: string]: {
      address: string | null;
      city: string | null;
      state: string | null;
      zip: string | null;
    };
  }>({});
  const [updatedInvoice, setUpdatedInvoice] = useState<
    {
      editedPayment?: number;
      formattedEditedPaymentValue?: string;
      invoiceOriginal: QuickbooksInvoice;
      discountAmount?: number;
      amountPaidWithCredits?: number;
    }[]
  >([]);
  const [openDiscountDropDown, setOpenDiscountDropDown] = useState(false);
  const [openCreditMemoDropDown, setOpenCreditMemoDropDown] = useState(false);
  const [supplierChargeWhoPaysFee, setSupplierChargeWhoPaysFee] = useState<
    'SUPPLIER' | 'BUYER' | 'BOTH'
  >('SUPPLIER');
  const [supplierTakeRate, setSupplierTakeRate] = useState(0);
  const { selectedSupplierForBuyer, selectedCreditMemos } = useAppSelector(
    (state) => state.invoice
  );
  const { loadingList, paymentError, weekLimitations } = useAppSelector((state) => state.payments);

  const buyerForMainBuyer = useMemo(() => {
    if (supplierChargeDetails) {
      return null;
    }
    const buyer = user?.userBuyers?.find(
      (buy) =>
        buy.supplierId === selectedSupplierForBuyer?.id &&
        selectedMainBuyer &&
        selectedMainBuyer.quickBooksCustomerId === buy.quickbooksCustomerId
    );
    return buyer;
  }, [selectedSupplierForBuyer, selectedMainBuyer, user?.userBuyers]);

  // Buyer settings win, supplier settings are the default. The supplier-charge flow carries its
  // own supplier/buyer pair, and `buyerForMainBuyer` is null there, so the two are exclusive.
  const effectiveWaiverConditions = useMemo(
    () =>
      getEffectiveCreditCardFeeWaiverConditions(
        supplierChargeDetails
          ? {
              supplier: supplierChargeDetails.supplier,
              buyer: supplierChargeDetails.customerDetails?.buyer,
            }
          : { supplier: selectedSupplierForBuyer, buyer: buyerForMainBuyer }
      ),
    [supplierChargeDetails, selectedSupplierForBuyer, buyerForMainBuyer]
  );

  // Mirrors the waiver branches in `calculateFiservFee`. Kept as one value so the strikethroughs
  // and the tooltip cannot disagree with the fee the backend actually charges.
  const waiverEarned = useMemo(() => {
    const hasCutoff = hasCurrentDueWaiverCutoff(
      effectiveWaiverConditions.currentDueWaiverDayOfMonthCutoff
    );
    const currentDue =
      isAllCurrentDue &&
      effectiveWaiverConditions.waiveForCurrentDuePayments === true &&
      (!hasCutoff || isWithinWaiverDayOfMonth);
    const discountDate =
      isAllWithinDiscountDate && effectiveWaiverConditions.waiveForDiscountDatePayments === true;
    return {
      currentDue,
      discountDate,
      // Which tooltip to show: the deadline is the interesting reason when one is configured.
      currentDueViaCutoff: currentDue && hasCutoff,
      any: currentDue || discountDate,
    };
  }, [
    effectiveWaiverConditions,
    isAllCurrentDue,
    isAllWithinDiscountDate,
    isWithinWaiverDayOfMonth,
  ]);

  const columns = useMemo(() => {
    let currentColumns = [
      { id: 'docNumber', name: 'Invoice #', width: 120, cellType: 'VIEW_TEXT' },
      { id: 'job', name: 'Job', width: 180, cellType: 'VIEW_TEXT' },
      { id: 'dueDate', name: 'Due Date', width: 150, cellType: 'VIEW_TEXT' },
      { id: 'balance', name: 'Open Amount', width: 180, cellType: 'VIEW_TEXT' },
      {
        id: 'payment',
        name: `${!supplierChargeDetails ? 'Payment' : 'Charge'}`,
        width: 180,
        cellType: 'VIEW_TEXT',
      },
      { id: 'actions', name: '', width: 80, cellType: 'VIEW_TEXT' },
    ];
    if (width < 800) {
      currentColumns = [
        { id: 'docNumber', name: 'Invoice #', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'balance', name: 'Open Amount', width: 200, cellType: 'VIEW_TEXT' },
        {
          id: 'payment',
          name: `${!supplierChargeDetails ? 'Payment' : 'Charge'}`,
          width: 200,
          cellType: 'VIEW_TEXT',
        },
        { id: 'actions', name: '', width: 80, cellType: 'VIEW_TEXT' },
      ];
    }
    return currentColumns;
  }, [editedInvoices]);

  const totalWidth = useMemo(
    () =>
      columns.reduce((curr, prev) => {
        return curr + prev.width;
      }, 0),
    [columns]
  );

  const partialPaymentReasons = useMemo(() => {
    const result: { label: string; value: string }[] = [];
    const supplier = supplierChargeDetails
      ? supplierChargeDetails.supplier
      : selectedSupplierForBuyer;
    if (supplier?.partialPaymentReasons && supplier?.partialPaymentReasons.length > 0) {
      for (let index = 0; index < supplier?.partialPaymentReasons.length; index++) {
        const element = supplier?.partialPaymentReasons[index];
        result.push({
          value: element,
          label: element,
        });
      }
      // "Tax Exemption" and "Pricing" are always offered, even for a supplier who hasn't added
      // them to their own reason list yet, since Dispute Management can route either regardless
      // of whether the supplier has customized this list.
      const configuredReasons = supplier.partialPaymentReasons;
      ['Tax Exemption', 'Pricing', 'Other'].forEach((defaultReason) => {
        if (!configuredReasons.includes(defaultReason)) {
          result.push({
            value: defaultReason,
            label: defaultReason,
          });
        }
      });
    }
    return result;
  }, [selectedSupplierForBuyer, supplierChargeDetails]);
  useEffect(() => {
    if (supplierChargeDetails) {
      setSupplierTakeRate(supplierChargeDetails.supplier?.creditCardTakeRate || 3.5);
    }

    return () => {};
  }, [supplierChargeDetails]);

  const creditCardTakeRate = useMemo(() => {
    const amount = totalPay - totalCreditsToUse;
    const buyer = user?.userBuyers?.find(
      (buy) =>
        buy.supplierId === selectedSupplierForBuyer?.id &&
        selectedMainBuyer &&
        selectedMainBuyer.quickBooksCustomerId === buy.quickbooksCustomerId
    );
    let calculateAmount = {} as {
      supplierFees: number;
      buyerFees: number;
      amount: number;
      amountToPay: number;
      totalFees: number;
    };
    if (selectedSupplierForBuyer && buyer) {
      calculateAmount = calculateStripePartialFacilitationFeesAndAmount({
        amount: 100,
        supplier: { ...selectedSupplierForBuyer, creditCardTakeRateExtra: 0 },
        buyer: { ...buyer, creditCardTakeRateExtra: 0 },
      });
    }
    return calculateAmount?.buyerFees ?? 0;
  }, [selectedPaymentMethod]);

  const showBankLimit = useMemo(() => {
    const result = paymentMethods.find((method) => method.id === selectedPaymentMethod);
    if (result) {
      return result.type === 'BANK_ACCOUNT';
    }
    return false;
  }, [selectedPaymentMethod]);

  /**
   * The day the money actually moves, which is what every discount window has to be measured
   * against. Undefined means "now" — the discount helpers default to that, so paying immediately
   * behaves exactly as before.
   */
  const discountEffectiveDate = useMemo(
    () => (paymentTiming === 'SCHEDULED' && scheduledDate ? scheduledDate : undefined),
    [paymentTiming, scheduledDate]
  );

  /**
   * Whether the submit button is queueing this payment rather than sending it. Requires both the
   * choice and a date, so a half-filled schedule never labels the button as though it were ready.
   */
  const isSchedulingPayment = useMemo(
    () => canSchedulePayment && paymentTiming === 'SCHEDULED' && Boolean(scheduledDate),
    [canSchedulePayment, paymentTiming, scheduledDate]
  );

  const isAnyWithinDiscountDate = useMemo(() => {
    const payDate = discountEffectiveDate ? moment(discountEffectiveDate).startOf('day') : moment();
    return editedInvoices.some(({ invoiceOriginal }) => {
      const discountDate = invoiceOriginal?.invoiceDiscountDate;
      if (!discountDate) return false;
      return payDate.isSameOrBefore(moment(discountDate).utc().endOf('day'));
    });
  }, [editedInvoices, discountEffectiveDate]);

  const selectedPaymentMethodDetails = useMemo(() => {
    const result = paymentMethods.find((method) => method.id === selectedPaymentMethod);
    if (result) {
      return result;
    }
    return null;
  }, [selectedPaymentMethod]);

  useEffect(() => {
    if (editedInvoices && editedInvoices.length > 0) {
      setInvoiceListErrorMessage('');
    }
  }, [editedInvoices]);

  const handleCloseModal = () => {
    setOpenDiscountDropDown(false);
    setOpenCreditMemoDropDown(false);
    onSelectPaymentMethod('');
    handleClose();
  };

  const checkCityStateZipCode = (temp: LienTemplate | null) => {
    if (temp) {
      const hasAddress = [...temp.formFields].filter(
        (formFields) =>
          formFields.fromFieldType === 'NEW_FIELD' &&
          formFields.whoPopulatesField === 'BUYER' &&
          formFields.id.startsWith('vendorCityStateZip')
      );
      return hasAddress.length > 0;
    }

    return false;
  };

  const checkVendorAddress = (temp: LienTemplate | null) => {
    if (temp) {
      const hasAddress = [...temp.formFields].filter(
        (formFields) =>
          formFields.fromFieldType === 'NEW_FIELD' &&
          formFields.whoPopulatesField === 'BUYER' &&
          formFields.id.startsWith('vendorAddress')
      );
      return hasAddress.length > 0;
    }

    return false;
  };

  const checkVendorPhone = (temp: LienTemplate | null) => {
    if (temp) {
      const hasPhone = [...temp.formFields].filter(
        (formFields) =>
          formFields.fromFieldType === 'NEW_FIELD' &&
          formFields.whoPopulatesField === 'BUYER' &&
          formFields.id.startsWith('vendorPhone')
      );
      return hasPhone.length > 0;
    }

    return false;
  };

  const checkJobOwner = (job: Job) => {
    if (job) {
      if (!job?.owner || job?.owner === '') {
        return true;
      }
    }
    return false;
  };

  const checkJobAddressCityStateZip = (job: Job, type: 'address' | 'city' | 'state' | 'zip') => {
    if (job) {
      if ((!job?.address || job?.address.trim() === '') && type === 'address') {
        return true;
      }
      if ((!job?.city || job?.city.trim() === '') && type === 'city') {
        return true;
      }
      if ((!job?.state || job?.state.trim() === '') && type === 'state') {
        return true;
      }
      if (!job?.zipCode && type === 'zip') {
        return true;
      }
    }
    return false;
  };

  const hasAddressJobCityStateZip = (job: Job) => {
    if (job) {
      if (
        !job?.city ||
        job?.city === '' ||
        !job?.state ||
        job?.state === '' ||
        !job?.zipCode ||
        !job?.address ||
        job?.address === ''
      ) {
        return true;
      }
    }
    return false;
  };

  const checkJobAddressCityStateZipChanges = (
    job: Job,
    originalJob: Job,
    type: 'address' | 'city' | 'state' | 'zip'
  ) => {
    if (job && originalJob) {
      if (type === 'address' && job?.address !== originalJob?.address) {
        return true;
      }
      if (type === 'city' && job?.city !== originalJob?.city) {
        return true;
      }
      if (type === 'state' && job?.state !== originalJob?.state) {
        return true;
      }
      if (type === 'zip' && originalJob?.zipCode !== originalJob?.zipCode) {
        return true;
      }
    }
    return false;
  };

  const handleUpdateRelease = async () => {
    if (releaseGroupDetails && selectedMainBuyerId) {
      const releaseGroupKeys = Object.keys(releaseGroupDetails).length;
      if (releaseGroupKeys > 0) {
        Object.entries(releaseGroupDetails).forEach(async ([key, value]) => {
          const jobOwnerRequired = checkJobOwner(value.originalJob);
          const hasCityStateZipCode = checkCityStateZipCode(value.template);
          const hasVendorAddress = checkVendorAddress(value.template);
          const hasVendorPhone = checkVendorPhone(value.template);

          const checkJobAddressChanges = checkJobAddressCityStateZipChanges(
            value.job,
            value.originalJob,
            'address'
          );
          const checkJobCityChanges = checkJobAddressCityStateZipChanges(
            value.job,
            value.originalJob,
            'city'
          );
          const checkJobStateChanges = checkJobAddressCityStateZipChanges(
            value.job,
            value.originalJob,
            'state'
          );
          const checkJobZipChanges = checkJobAddressCityStateZipChanges(
            value.job,
            value.originalJob,
            'zip'
          );

          const updateJobAddress = hasAddressJobCityStateZip(value.originalJob);

          let updateJobDetails = {};
          if (jobOwnerRequired) {
            updateJobDetails = { ...updateJobDetails, owner: value.job?.owner ?? '' };
          }
          if (updateJobAddress && checkJobAddressChanges) {
            updateJobDetails = { ...updateJobDetails, address: value.job?.address ?? '' };
          }
          if (updateJobAddress && checkJobCityChanges) {
            updateJobDetails = { ...updateJobDetails, city: value.job?.city ?? '' };
          }
          if (updateJobAddress && checkJobStateChanges) {
            updateJobDetails = { ...updateJobDetails, state: value.job?.state ?? '' };
          }
          if (updateJobAddress && checkJobZipChanges) {
            updateJobDetails = { ...updateJobDetails, zipCode: Number(value.job?.address) ?? null };
          }

          if (jobOwnerRequired || updateJobAddress) {
            const updateJobResult = await dispatch(
              updateJob({
                mainBuyerId: selectedMainBuyerId,
                jobId: value.job?.id ?? '',
                jobDetails: { ...updateJobDetails },
              })
            );
            if (updateJobResult.type === 'jobs/updateJob/fulfilled') {
              await dispatch(getJobs({ mainBuyerId: selectedMainBuyerId }));
            }
          }
          let updateVendorDetails = {};
          if (hasCityStateZipCode) {
            updateVendorDetails = {
              ...updateVendorDetails,
              city: vendorDetail[key].city,
              state: vendorDetail[key].state,
              zipCode: Number(vendorDetail[key].zip),
            };
          }
          if (hasVendorAddress) {
            updateVendorDetails = { ...updateVendorDetails, address: vendorDetail[key].address };
          }
          if (hasVendorPhone && value.formFields) {
            const vendorPhoneEntry = Object.entries(value.formFields).find(([key2, value2]) =>
              key2.startsWith('vendorPhone')
            );
            if (vendorPhoneEntry) {
              const [vendorPhoneKey, vendorPhoneObject] = vendorPhoneEntry;
              updateVendorDetails = {
                ...updateVendorDetails,
                phoneNumber: vendorPhoneObject?.value ?? '',
              };
            }
          }
          if (Object.keys(updateVendorDetails).length > 0) {
            await dispatch(
              updateSubSupplier({
                mainBuyerId: selectedMainBuyerId || '',
                subSupplierDetails: { ...updateVendorDetails },
                subSupplierId: releaseData[Number(key)]?.subSupplierId ?? '',
              })
            );
          }
        });
      }
    }
  };

  const handleError = () => {
    let error: { [key: string]: { [key: string]: string } } = {};
    Object.entries(releaseGroupDetails).forEach(([key, value]) => {
      const jobOwnerRequired = checkJobOwner(value.originalJob);
      const hasJobOwner = checkJobOwner(value.job);
      const hasJobAddress = checkJobAddressCityStateZip(value.job, 'address');
      const hasJobCity = checkJobAddressCityStateZip(value.job, 'city');
      const hasJobState = checkJobAddressCityStateZip(value.job, 'state');
      const hasJobZip = checkJobAddressCityStateZip(value.job, 'zip');
      const hasVendorAddress = checkVendorAddress(value.template);
      const hasCityStateZipCode = checkCityStateZipCode(value.template);
      let fields: string[] = [];
      if (jobOwnerRequired && hasJobOwner) {
        error = { ...error, [key]: { ...error[key], owner: 'Owner is required' } };
      }
      if (hasJobAddress) {
        error = { ...error, [key]: { ...error[key], address: 'Job address is required' } };
      }
      if (hasJobCity) {
        error = { ...error, [key]: { ...error[key], city: 'Job city is required' } };
      }
      if (hasJobState) {
        error = { ...error, [key]: { ...error[key], state: 'Job state is required' } };
      }
      if (hasJobZip) {
        error = { ...error, [key]: { ...error[key], zipCode: 'Job zip code is required' } };
      }
      if (value.job === null || value.job === undefined) {
        error = { ...error, [key]: { ...error[key], job: 'Select job' } };
      }
      if (value.template === null || value.template === undefined) {
        error = { ...error, [key]: { ...error[key], template: 'Select template' } };
      }
      if (value.subSupplierPosition === null || value.subSupplierPosition === undefined) {
        error = { ...error, [key]: { ...error[key], supplier: 'Select supplier' } };
      }
      if (
        hasVendorAddress &&
        (!vendorDetail[key]?.address ||
          vendorDetail[key]?.address === null ||
          vendorDetail[key]?.address === '')
      ) {
        fields = [...fields, 'address'];
      }
      if (
        hasCityStateZipCode &&
        (!vendorDetail[key]?.city ||
          vendorDetail[key]?.city === null ||
          vendorDetail[key]?.city === '')
      ) {
        fields = [...fields, 'city'];
      }
      if (
        hasCityStateZipCode &&
        (!vendorDetail[key]?.state ||
          vendorDetail[key]?.state === null ||
          vendorDetail[key]?.state === '')
      ) {
        fields = [...fields, 'state'];
      }
      if (
        hasCityStateZipCode &&
        (!vendorDetail[key]?.zip ||
          vendorDetail[key]?.zip === null ||
          vendorDetail[key]?.zip === '')
      ) {
        fields = [...fields, 'zipCode'];
      }
      if (value.formFields) {
        Object.keys(value.formFields).forEach((fieldKey) => {
          const fieldData = value.formFields[fieldKey];
          if (
            fieldData.isRequired &&
            !fieldData.value &&
            !fieldData.id.startsWith('vendorCityStateZip') &&
            !fieldData.id.startsWith('vendorAddress')
          ) {
            fields = [...fields, fieldKey];
          }
        });
      }
      for (let index = 0; index < fields.length; index++) {
        const field = fields[index];
        error = { ...error, [key]: { ...error[key], [field]: 'Required' } };
      }
    });
    return error;
  };

  const creditDiscountUsed = useMemo(() => {
    if (!isUseCredits) return [];
    const supplierToUse = supplierChargeDetails ? currentSupplier : selectedSupplierForBuyer;

    if (
      supplierToUse &&
      (supplierToUse?.integrationType === 'MANUAL_UPLOAD' ||
        supplierToUse?.integrationType === 'EAGLE_MANUAL_UPLOAD' ||
        supplierToUse?.integrationType === 'DDI_MANUAL_UPLOAD' ||
        supplierToUse?.integrationType === 'ECLIPSE' ||
        supplierToUse?.integrationType === 'ECLIPSE_MANUAL_UPLOAD' ||
        supplierToUse?.integrationType === 'MARKS_MANUAL_UPLOAD' ||
        (supplierToUse?.integrationType === 'P21_MANUAL_UPLOAD' &&
          supplierToUse?.domainId === 'carolinasupply') ||
        (supplierToUse?.integrationType === 'SAGE_100' && supplierToUse?.domainId === 'topgun') ||
        (supplierToUse?.integrationType === 'INFOR_DIST_SX' &&
          !!supplierToUse?.discountSettings?.useErpTermsDiscount) ||
        selectedSupplierForBuyer?.integrationType === 'DISTRIBUTION_ONE' ||
        (supplierToUse?.integrationType === 'RUTTER_QBD' && APP_ENVIRONMENT !== 'production')) &&
      isSelectPaymentMethod &&
      (selectedMainBuyer || supplierChargeDetails)
    ) {
      const result = paymentMethods.find((method) => method.id === selectedPaymentMethod);
      if (
        result &&
        (result.type === 'BANK_ACCOUNT' || supplierToUse.discountSettings?.isDiscountOnCardPayments)
      ) {
        const creditMemos = Object.values(selectedCreditMemos).flat();
        return creditMemos
          .map((credit) => {
            const { discountAmount } = getCreditDiscountInfo(
              credit,
              supplierToUse?.arSettings?.discountOnCredit,
              discountEffectiveDate
            );
            let creditId = credit.id;
            if (supplierToUse.integrationType === 'ECLIPSE') {
              creditId = credit.docNumber;
            }
            return { creditId: creditId, creditAmount: discountAmount };
          })
          .filter((item) => item.creditAmount > 0);
      }
    }
    return [];
  }, [
    selectedCreditMemos,
    isUseCredits,
    selectedPaymentMethod,
    isSelectPaymentMethod,
    discountEffectiveDate,
  ]);

  const handlePayment = async () => {
    const releaseError = handleError();
    setReleaseErrors(releaseError);
    setOpenCreditMemoDropDown(false);
    if (Object.keys(releaseError).length > 0 && isSelectPaymentMethod && sendRealese) {
      return;
    }
    if (selectedPaymentMethod == '' && isSelectPaymentMethod) {
      return;
    }
    if (isSelectPaymentMethod) {
      if (
        weekLimitations &&
        totalPay - totalCreditsToUse + fees > weekLimitations?.weekLimit &&
        showBankLimit
      ) {
        setInvoiceListErrorMessage(
          `Total payment is greater than this week's limit of ${formatMoney(
            weekLimitations?.weekLimit,
            2
          )}. Please auto-adjust the invoices to complete payment, and we will send you a reminder next week to pay the rest.`
        );
        dispatch(
          dwollaTransactionLimitNotifyDill({
            buyerId: selectedBuyerId || '',
            supplierId: selectedSupplierForBuyer?.id || '',
          })
        );
        setIsAutoAdjustInvoicesToFitLimit(true);
      } else {
        if (sendRealese) {
          onReleaseDetails(releaseData, releaseGroupDetails);
        }
        setInvoiceListErrorMessage('');
        onChangeModalType('paymentConfirmation');
        handleUpdateRelease();
        onPaymentDetail(
          totalCreditsToUse,
          selectedPaymentMethod,
          partialPaymentReason,
          updatedInvoice,
          creditDiscountUsed
        );
      }
    } else if (isPartialPayment && partialPaymentReason === '' && !supplierChargeDetails) {
      setInvoiceListErrorMessage('Please select a reason for short payment');
    } else if (modalType === 'invoiceList') {
      onToggleIsPaymentMethod(true);
    } else {
      onToggleIsPaymentMethod(true);
      setInvoiceListErrorMessage('');
    }
  };

  const handleCreateRelease = (
    data: {
      [x: string]: {
        job: Job;
        originalJob: Job;
        template: LienTemplate | null;
        currentPdfUrl: string;
        originalPdfUrl: string;
        subSupplierPosition: number | null;
        amount: number;
        formFields: { [x: string]: ReleaseFieldData };
        templateFields: ReleaseTemplateField[] | null;
      };
    },
    vendorDetails: {
      [x: string]: {
        address: string | null;
        city: string | null;
        state: string | null;
        zip: string | null;
      };
    },
    releaseArray: LienApiPostBody[]
  ) => {
    setReleaseGroupDetails(data);
    setVendorDetail(vendorDetails);
    setReleaseData(releaseArray);
  };

  useEffect(() => {
    if (partialReason) {
      setPartialPaymentReason(partialReason);
    }
  }, []);

  useEffect(() => {
    onPartialPaymentDetailsChange({
      notes: partialPaymentNotes,
      urls: partialPaymentAttachmentsUrls,
      files: partialPaymentAttachmentsFiles,
    });
  }, [partialPaymentNotes, partialPaymentAttachmentsUrls, partialPaymentAttachmentsFiles]);

  const handleDeletePaymentMethod = async (paymentMethod: DillPaymentMethod) => {
    const isBankAccount = paymentMethod.type === 'BANK_ACCOUNT';
    const warningMessage = isBankAccount
      ? 'This action cannot be undone. If you delete a bank method and decide to re-add the same one, you will need to wait 48 hours.'
      : 'This action can not be undone.';
    if (!supplierChargeDetails) {
      dispatch(
        openMessageModal({
          buttonText: 'Cancel',
          isSecondButton: true,
          secondButtonText: 'Delete',
          modalType: 'WARN',
          title: `Are you sure you want to delete this payment method?`,
          subTitle: warningMessage,
          onSecondButtonClick: async () => {
            await appDelay(10);
            dispatch(
              openMessageModal({
                buttonText: '',
                modalType: 'LOADING',
                title: 'Deleting',
                subTitle: 'Deleting payment method',
              })
            );
            const deletePaymentMethodResults = await dispatch(
              deletePaymentMethodByMainBuyer({
                mainBuyerId: selectedMainBuyerId || '',
                paymentMethodId: paymentMethod.id,
              })
            );
            if (deletePaymentMethodResults.type === 'payments/deletePaymentMethod/rejected') {
              let errorMessage = '';
              if (deletePaymentMethodResults.type === 'payments/deletePaymentMethod/rejected') {
                errorMessage = deletePaymentMethodResults.payload as any;
              }
              console.log({ errorMessage });
              dispatch(
                openMessageModal({
                  buttonText: 'Close',
                  modalType: 'ERROR',
                  title: 'Error deleting payment method',
                  subTitle: `${errorMessage} Contact (628) 304-0931 for customer support`,
                })
              );
            } else if (
              deletePaymentMethodResults.type === 'payments/deletePaymentMethod/fulfilled'
            ) {
              dispatch(
                openMessageModal({
                  buttonText: 'Close',
                  modalType: 'SUCCESS',
                  title: 'Payment method deleted',
                  subTitle: `Payment method deleted successfully`,
                  onClose: () => {
                    dispatch(
                      getMainBuyerPaymentMethods({ mainBuyerId: selectedMainBuyerId || '' })
                    );
                  },
                })
              );
              setOpenDiscountDropDown(false);
              setOpenCreditMemoDropDown(false);
            } else {
              closeMessageModal();
            }
          },
        })
      );
    } else {
      dispatch(
        openMessageModal({
          buttonText: 'Cancel',
          isSecondButton: true,
          secondButtonText: 'Delete',
          modalType: 'WARN',
          title: `Are you sure you want to delete this payment method?`,
          subTitle: warningMessage,
          onSecondButtonClick: async () => {
            await appDelay(10);
            dispatch(
              openMessageModal({
                buttonText: '',
                modalType: 'LOADING',
                title: 'Deleting',
                subTitle: 'Deleting payment method',
              })
            );
            const paymentMethodDeleteResponse = await dispatch(
              deleteSupplierCustomerPaymentMethod({
                supplierId: currentSupplier?.id || '',
                customerId: supplierChargeDetails.customerDetails.buyer.id,
                paymentMethodId: paymentMethod.id,
              })
            );
            if (
              paymentMethodDeleteResponse.type ===
              'payments/deleteSupplierCustomerPaymentMethod/fulfilled'
            ) {
              dispatch(
                openMessageModal({
                  buttonText: 'Close',
                  modalType: 'SUCCESS',
                  title: 'Payment method deleted',
                  subTitle: `Payment method deleted successfully`,
                  onClose: () => {
                    if (supplierChargeDetails) {
                      dispatch(
                        getSupplierCustomerPaymentMethods({
                          supplierId: currentSupplier?.id || '',
                          customerId: supplierChargeDetails.customerDetails.buyer.id,
                        })
                      );
                    }
                  },
                })
              );
            } else {
              let errorMessage = '';
              if (
                paymentMethodDeleteResponse.type ===
                'payments/deleteSupplierCustomerPaymentMethod/rejected'
              ) {
                errorMessage = paymentMethodDeleteResponse.payload as any;
              }
              console.log({ errorMessage });
              dispatch(
                openMessageModal({
                  buttonText: 'Close',
                  modalType: 'ERROR',
                  title: 'Error deleting payment method',
                  subTitle: `${errorMessage} Contact (628) 304-0931 for customer support`,
                })
              );
            }
          },
        })
      );
    }
  };

  const getPaymentDetails = (
    invoices: {
      editedPayment?: number;
      discountAmount?: number;
      formattedEditedPaymentValue?: string;
      invoiceOriginal: QuickbooksInvoice;
    }[]
  ) => {
    const adjustedSelectedInvoices = invoices.map((editedInvoice) => {
      return {
        ...editedInvoice,
        invoiceOriginal: {
          ...editedInvoice.invoiceOriginal,
          id: !editedInvoice?.invoiceOriginal?.recordType
            ? editedInvoice.invoiceOriginal?.id
            : editedInvoice.invoiceOriginal?.docNumber ?? '',
        },
      };
    });
    const payInvoice: InvoicePaymentDetails[] = [];
    for (let index = 0; index < adjustedSelectedInvoices.length; index++) {
      const currentInvoice = adjustedSelectedInvoices[index].invoiceOriginal;
      const discountAmountValue = adjustedSelectedInvoices[index].discountAmount ?? 0;
      const paidAmountValue = adjustedSelectedInvoices[index].editedPayment ?? 0;
      const paidAmountAfterDiscount = paidAmountValue - discountAmountValue;
      payInvoice.push({
        amount: currentInvoice?.amount ?? 0,
        quickbooksInvoiceId: currentInvoice.platformInvoiceId || '',
        providerInvoiceId: currentInvoice.id,
        quickbooksJobId: currentInvoice?.quickBooksJob?.platformJobId || '',
        providerJobId: currentInvoice?.quickBooksJob?.id || '',
        invoiceNumber: currentInvoice.docNumber,
        amountPaid: Number(paidAmountAfterDiscount.toFixed(2)),
        dueDate: currentInvoice.dueDate,
        openAmount: currentInvoice.balance,
        recordType: currentInvoice.recordType ? currentInvoice.recordType : ('INVOICE' as const),
        discountAmount: Number(discountAmountValue.toFixed(2)),
        jobId: currentInvoice?.jobId || '',
        customerId: currentInvoice?.customerId || '',
      });
    }
    return payInvoice;
  };

  useEffect(() => {
    if (supplierChargeBuyerTakeRate === 0) {
      setSupplierChargeWhoPaysFee('SUPPLIER');
      onSupplierChargeWhoPaysFee('SUPPLIER');
    } else if (supplierChargeBuyerTakeRate === supplierTakeRate) {
      setSupplierChargeWhoPaysFee('BUYER');
      onSupplierChargeWhoPaysFee('BUYER');
    } else {
      setSupplierChargeWhoPaysFee('BOTH');
      onSupplierChargeWhoPaysFee('BOTH');
    }

    return () => {};
  }, [supplierChargeBuyerTakeRate]);

  function calculateDiscountsForInvoices(
    invoices: {
      editedPayment?: number;
      formattedEditedPaymentValue?: string;
      invoiceOriginal: QuickbooksInvoice;
    }[]
  ): number {
    const supplierToUse = supplierChargeDetails ? currentSupplier : selectedSupplierForBuyer;
    const updateInv = invoices.map((inv) => {
      const discountInfo = getDiscountInfo(
        inv,
        supplierToUse?.integrationType,
        supplierToUse?.domainId,
        discountEffectiveDate
      );
      return { ...inv, discountAmount: discountInfo.discountAmount };
    });

    setUpdatedInvoice(updateInv);
    return invoices.reduce((total, invoice) => {
      const discountInfo = getDiscountInfo(
        invoice,
        supplierToUse?.integrationType,
        supplierToUse?.domainId,
        discountEffectiveDate
      );

      return total + discountInfo.discountAmount;
    }, 0);
  }

  const discountPayment = useMemo(() => {
    const supplierToUse = supplierChargeDetails ? currentSupplier : selectedSupplierForBuyer;
    if (
      supplierToUse &&
      (supplierToUse?.integrationType === 'MANUAL_UPLOAD' ||
        supplierToUse?.integrationType === 'EAGLE_MANUAL_UPLOAD' ||
        supplierToUse?.integrationType === 'DDI_MANUAL_UPLOAD' ||
        supplierToUse?.integrationType === 'MARKS_MANUAL_UPLOAD' ||
        supplierToUse?.integrationType === 'ECLIPSE' ||
        supplierToUse?.integrationType === 'ECLIPSE_MANUAL_UPLOAD' ||
        (supplierToUse?.integrationType === 'P21_MANUAL_UPLOAD' &&
          supplierToUse?.domainId === 'carolinasupply') ||
        (supplierToUse?.integrationType === 'SAGE_100' && supplierToUse?.domainId === 'topgun') ||
        (supplierToUse?.integrationType === 'INFOR_DIST_SX' &&
          !!supplierToUse?.discountSettings?.useErpTermsDiscount) ||
        selectedSupplierForBuyer?.integrationType === 'DISTRIBUTION_ONE' ||
        (supplierToUse?.integrationType === 'RUTTER_QBD' && APP_ENVIRONMENT !== 'production')) &&
      isSelectPaymentMethod &&
      (selectedMainBuyer || supplierChargeDetails)
    ) {
      const result = paymentMethods.find((method) => method.id === selectedPaymentMethod);
      if (
        result &&
        (result.type === 'BANK_ACCOUNT' || supplierToUse.discountSettings?.isDiscountOnCardPayments)
      ) {
        const discountAmount = calculateDiscountsForInvoices(editedInvoices);
        return discountAmount;
      } else {
        setUpdatedInvoice(editedInvoices.map((invoice) => ({ ...invoice, discountAmount: 0 })));
      }
    } else {
      setUpdatedInvoice(editedInvoices.map((invoice) => ({ ...invoice, discountAmount: 0 })));
    }
    return 0;
  }, [editedInvoices, selectedPaymentMethod, isSelectPaymentMethod, discountEffectiveDate]);

  const creditDiscountTotal = useMemo(() => {
    if (!isUseCredits) return 0;
    const supplierToUse = supplierChargeDetails ? currentSupplier : selectedSupplierForBuyer;
    if (
      supplierToUse &&
      (supplierToUse?.integrationType === 'MANUAL_UPLOAD' ||
        supplierToUse?.integrationType === 'EAGLE_MANUAL_UPLOAD' ||
        supplierToUse?.integrationType === 'DDI_MANUAL_UPLOAD' ||
        supplierToUse?.integrationType === 'MARKS_MANUAL_UPLOAD' ||
        supplierToUse?.integrationType === 'ECLIPSE' ||
        supplierToUse?.integrationType === 'ECLIPSE_MANUAL_UPLOAD' ||
        (supplierToUse?.integrationType === 'P21_MANUAL_UPLOAD' &&
          supplierToUse?.domainId === 'carolinasupply') ||
        (supplierToUse?.integrationType === 'SAGE_100' && supplierToUse?.domainId === 'topgun') ||
        (supplierToUse?.integrationType === 'INFOR_DIST_SX' &&
          !!supplierToUse?.discountSettings?.useErpTermsDiscount) ||
        selectedSupplierForBuyer?.integrationType === 'DISTRIBUTION_ONE' ||
        (supplierToUse?.integrationType === 'RUTTER_QBD' && APP_ENVIRONMENT !== 'production')) &&
      isSelectPaymentMethod &&
      (selectedMainBuyer || supplierChargeDetails)
    ) {
      const result = paymentMethods.find((method) => method.id === selectedPaymentMethod);
      if (
        result &&
        (result.type === 'BANK_ACCOUNT' || supplierToUse.discountSettings?.isDiscountOnCardPayments)
      ) {
        const creditMemos = Object.values(selectedCreditMemos).flat();
        return creditMemos.reduce((total, credit) => {
          const { discountAmount } = getCreditDiscountInfo(
            credit,
            supplierToUse?.arSettings?.discountOnCredit,
            discountEffectiveDate
          );
          return total + discountAmount;
        }, 0);
      }
    }
    return 0;
  }, [
    selectedCreditMemos,
    isUseCredits,
    selectedPaymentMethod,
    isSelectPaymentMethod,
    discountEffectiveDate,
  ]);

  const renderDiscountDropDown = () => {
    const supplierToUse = supplierChargeDetails ? currentSupplier : selectedSupplierForBuyer;
    const creditMemosWithDiscount = Object.values(selectedCreditMemos)
      .flat()
      .filter(
        (c) =>
          getCreditDiscountInfo(
            c,
            supplierToUse?.arSettings?.discountOnCredit,
            discountEffectiveDate
          ).discountAmount > 0
      );
    const totalDiscount = discountPayment - creditDiscountTotal;

    const discountPaymentText =
      totalDiscount > 0
        ? `-${formatMoney(Math.abs(totalDiscount), 2)}`
        : `+${formatMoney(Math.abs(totalDiscount), 2)}`;
    return (
      <div className="PRIMARY_300-BORDER border mb-2 rounded overflow-hidden">
        <div
          className="flex flex-row justify-between  cursor-pointer p-1 hover:bg-indigo-100"
          onClick={() => setOpenDiscountDropDown(!openDiscountDropDown)}>
          <div className="flex items-center justify-center">
            {openDiscountDropDown ? (
              <CaretUp color={COLORS.GREY_500} size={19} />
            ) : (
              <CaretDown color={COLORS.GREY_500} size={19} />
            )}
            <div className="amount-label-text ml-2">Discount</div>
          </div>
          <div className="amount-text">{discountPaymentText}</div>
        </div>
        {openDiscountDropDown && (
          <div className="WHITE-BG p-2">
            {discountPayment > 0 && (
              <>
                <div className="w-full px-[4px] py-1 flex justify-between PRIMARY_100-BG">
                  <div className="text-xs font-bold">Invoice No</div>
                  <div className="flex">
                    <div className="text-xs w-[100px] font-bold">Due Date</div>
                    <div className="text-xs w-[120px] font-bold">Amount</div>
                    <div className="text-xs w-[100px] font-bold">Terms</div>
                    <div className="text-xs w-[100px] flex justify-end font-bold">Discount</div>
                  </div>
                </div>
                {updatedInvoice.map((invoice) => {
                  const discount = invoice?.discountAmount ?? 0;
                  const amountPaidWithCredit = invoice?.amountPaidWithCredits ?? 0;
                  const editedPayment = invoice?.editedPayment ?? 0;
                  const amountPaid = editedPayment - amountPaidWithCredit;
                  return (
                    <div
                      className="w-full px-[2px] py-1 flex justify-between"
                      key={`dropdown_discount_${invoice.invoiceOriginal.id}`}>
                      <div className="text-xs">
                        {invoice?.invoiceOriginal?.docNumber || invoice.invoiceOriginal.id}
                      </div>
                      <div className="flex">
                        <div className={`text-xs w-[100px] ${discount > 0 ? '' : 'ERROR_500-CLR'}`}>
                          {moment(invoice.invoiceOriginal.dueDate).format('MM/DD/YYYY')}
                        </div>
                        <div className="text-xs w-[120px]">{`${formatMoney(
                          invoice?.editedPayment ?? '',
                          2
                        )}`}</div>
                        <div className="text-xs w-[100px]">
                          {discount > 0
                            ? getDiscountInfo(
                                invoice,
                                supplierToUse?.integrationType,
                                supplierToUse?.domainId,
                                discountEffectiveDate
                              ).discountTerms
                            : ''}
                        </div>
                        <div className="text-xs w-[100px] flex justify-end">{`-${formatMoney(
                          Math.abs(discount),
                          2
                        )}`}</div>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
            {creditMemosWithDiscount.length > 0 && (
              <>
                <div className="w-full px-[4px] py-1 flex justify-between PRIMARY_100-BG mt-1">
                  <div className="text-xs font-bold">Credit No</div>
                  <div className="flex">
                    <div className="text-xs w-[100px] font-bold">Due Date</div>
                    <div className="text-xs w-[120px] font-bold">Amount</div>
                    <div className="text-xs w-[100px] font-bold">Terms</div>
                    <div className="text-xs w-[100px] flex justify-end font-bold">Discount</div>
                  </div>
                </div>
                {creditMemosWithDiscount.map((creditMemo, i) => {
                  const { discountAmount, discountTerms } = getCreditDiscountInfo(
                    creditMemo,
                    supplierToUse?.arSettings?.discountOnCredit,
                    discountEffectiveDate
                  );
                  return (
                    <div
                      key={`dropdown_discount_credit_${creditMemo.id}_${i}`}
                      className="w-full px-[2px] py-1 flex justify-between">
                      <div className="text-xs">{creditMemo.docNumber}</div>
                      <div className="flex">
                        <div className="text-xs w-[100px]">
                          {creditMemo.dueDate
                            ? moment(creditMemo.dueDate).format('MM/DD/YYYY')
                            : ''}
                        </div>
                        <div className="text-xs w-[120px]">{`${formatMoney(
                          creditMemo.balance ?? '',
                          2
                        )}`}</div>
                        <div className="text-xs w-[100px]">{discountTerms}</div>
                        <div className="text-xs w-[100px] flex justify-end">{`+${formatMoney(
                          Math.abs(discountAmount),
                          2
                        )}`}</div>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderCreditDropDown = () => {
    const creditMemos = Object.values(selectedCreditMemos).flat();
    return (
      <div className="PRIMARY_300-BORDER border mb-2 rounded overflow-hidden">
        <div
          className="flex flex-row justify-between  cursor-pointer p-1 hover:bg-indigo-100"
          onClick={() => setOpenCreditMemoDropDown(!openCreditMemoDropDown)}>
          <div className="flex items-center justify-center">
            {openCreditMemoDropDown ? (
              <CaretUp color={COLORS.GREY_500} size={19} />
            ) : (
              <CaretDown color={COLORS.GREY_500} size={19} />
            )}
            <div className="amount-label-text ml-2">Credits</div>
          </div>
          <div className="amount-text">{`-${formatMoney(totalCreditsToUse, 2)}`}</div>
        </div>
        {openCreditMemoDropDown && (
          <div className="WHITE-BG p-2">
            <div className="w-full px-[4px] py-1 flex justify-between PRIMARY_100-BG">
              <div className="text-xs font-bold">Doc No#</div>
              <div className="flex">
                <div className="text-xs w-[100px] flex justify-end font-bold">Amount</div>
              </div>
            </div>
            {creditMemos.map((creditMemo, i) => {
              return (
                <div
                  key={`payment_credit_memo_dropdown_${creditMemo.id}_${i}`}
                  className="w-full px-[2px] py-1 flex justify-between">
                  <div className="text-xs">{creditMemo.docNumber}</div>
                  <div className="flex">
                    <div className="text-xs w-[100px] flex justify-end">{`-${formatMoney(
                      creditMemo.balance ?? '',
                      2
                    )}`}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="app-payment-invoice-table-wrapper ">
      {!supplierChargeDetails && (
        <AppAddPaymentMethodModal
          userType="buyer"
          open={isAppAddPaymentMethodModalOpen}
          paymentOptionsToShow={
            selectedSupplierForBuyer?.isOptoutCardPayments
              ? 'BANK'
              : selectedSupplierForBuyer?.cardPaymentProvider === 'FISERV'
              ? 'BANK_AND_FISERV_CARD'
              : 'ALL'
          }
          isDebitCardDisabled={
            selectedSupplierForBuyer?.isDebitCardDisabled ||
            (buyerForMainBuyer &&
              buyerForMainBuyer?.disabledPaymentTypes &&
              buyerForMainBuyer.disabledPaymentTypes?.includes('DEBIT_CARD'))
              ? true
              : false
          }
          isCreditCardDisabled={
            buyerForMainBuyer &&
            buyerForMainBuyer?.disabledPaymentTypes &&
            buyerForMainBuyer?.disabledPaymentTypes?.includes('CREDIT_CARD')
              ? true
              : false
          }
          isBankDisabled={
            buyerForMainBuyer &&
            buyerForMainBuyer?.disabledPaymentTypes &&
            buyerForMainBuyer?.disabledPaymentTypes?.includes('BANK')
              ? true
              : false
          }
          handleClose={() => {
            dispatch(getMainBuyerPaymentMethods({ mainBuyerId: selectedMainBuyerId || '' }));
            setIsAppAddPaymentMethodModalOpen(false);
          }}
        />
      )}
      {supplierChargeDetails && (
        <AppAddPaymentMethodModal
          userType="supplier"
          paymentOptionsToShow={
            supplierChargeDetails?.supplier?.cardPaymentProvider === 'FISERV'
              ? 'FISERV_CARD'
              : 'CARD'
          }
          open={isAppAddPaymentMethodModalOpen}
          buyerDetails={supplierChargeDetails.customerDetails}
          handleClose={() => {
            setIsAppAddPaymentMethodModalOpen(false);
            if (supplierChargeDetails) {
              dispatch(
                getSupplierCustomerPaymentMethods({
                  supplierId: currentSupplier?.id || '',
                  customerId: supplierChargeDetails.customerDetails.buyer.id,
                })
              );
            }
          }}
        />
      )}
      <AppVerifyDwollaMicroTransactionsModal
        selectedPaymentMethod={selectedPaymentMethodToVerifyMicroTransactions}
        open={isAppVerifyDwollaMicroTransactionsModalOpen}
        handleClose={() => {
          dispatch(getMainBuyerPaymentMethods({ mainBuyerId: selectedMainBuyerId || '' }));
          setSelectedPaymentMethodToVerifyMicroTransactions(undefined);
          setIsAppVerifyDwollaMicroTransactionsModalOpen(false);
        }}
      />
      <div className="payment-modal-title-container sticky top-0 bg-white z-10">
        <div className="flex">
          <h3 className="font-medium text-lg">
            {isSelectPaymentMethod
              ? `${formatMoney(totalPay + fees, 2)}`
              : supplierChargeDetails
              ? `${editedInvoices.length > 1 ? 'Bulk Charge' : 'Charge Invoice'}`
              : `${editedInvoices.length > 1 ? 'Bulk Pay' : 'Pay Invoice'}`}
          </h3>
        </div>
        <DillButton variant="ghost" icon={<X size={20} />} onClick={handleCloseModal} />
      </div>
      <div className="payment-invoice-table-modal-container">
        <div className="flex flex-row">
          <div className="payment-invoice-table-modal-left-content ">
            <div className="flex flex-col mx-6 mt-4">
              <div className="flex flex-row w-full my-1 items-baseline">
                <p className="text-lg w-1/5">To</p>
                <p className="text-sm font-bold capitalize w-4/5">{paymentTo}</p>
              </div>
              <div className="flex flex-row w-full my-1 items-baseline">
                <p className="text-lg w-1/5">From</p>
                <p className="text-sm font-bold capitalize w-4/5">{paymentFrom}</p>
              </div>
            </div>
            <div className="flex flex-col px-1 md:px-4 mt-4 grow">
              {!isSelectPaymentMethod && editedInvoices.length === 1 && (
                <div className="mt-1 mb-4 flex items-center gap-1 p-2 rounded-lg bg-indigo-50 border border-indigo-200 ">
                  <div className="flex-shrink-0 mt-0.5">
                    <LightbulbFilament color={COLORS.PRIMARY_500} size={20} weight="fill" />
                  </div>
                  <p className="text-xs PRIMARY_700-CLR font-medium">
                    You can multiselect to pay for multiple invoices at once
                  </p>
                </div>
              )}

              {!isSelectPaymentMethod && isAnyWithinDiscountDate && (
                <div className="mb-4 flex items-center gap-1 p-2 rounded-lg bg-amber-50 border border-amber-200 ">
                  <div className="flex-shrink-0 mt-0.5">
                    <LightbulbFilament color={COLORS.WARNING_500} size={20} weight="fill" />
                  </div>
                  <p className="text-xs text-amber-700 font-medium">
                    <span className="font-bold">Possible Discount:</span> Any discounts will get
                    automatically applied for you when you select your payment method in the next
                    step. They will not show yet at this step.
                  </p>
                </div>
              )}
              {!isSelectPaymentMethod && (
                <div className="flex w-full flex-col overflow-y-scroll border max-h-64 rounded-xl">
                  <div className="flex border-b px-5 items-center w-full sticky top-0 PRIMARY_50-BG py-3">
                    {columns.map((column) => {
                      return (
                        <div
                          key={column.id}
                          style={{ flex: column.width / totalWidth }}
                          className="flex w-full  ">
                          {column.cellType !== 'SELECT' && (
                            <p className="TEXT_SECONDARY-CLR text-xs w-full text-left">
                              {column.name}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {editedInvoices.length < 1 && (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="flex flex-col items-center">
                        <img className="w-3/6 object-contain" src={emptyListImg} alt="" />
                        <p>No invoices</p>
                      </div>
                    </div>
                  )}
                  {editedInvoices.length > 0 &&
                    [...editedInvoices].map((invoiceEdited, invoiceIndex) => {
                      return (
                        <div key={invoiceIndex + 'oo'} className={'flex items-center px-1 my-2'}>
                          {columns.map((column) => {
                            type ObjectKey = keyof typeof invoiceEdited.invoiceOriginal;
                            const columnKey = column.id as ObjectKey;
                            let value = invoiceEdited.invoiceOriginal[columnKey]?.toString();
                            const columnInvoiceNo = invoiceEdited.invoiceOriginal.id?.toString();
                            if (column.id === 'createdAt' || column.id === 'dueDate') {
                              value = moment(value).utc().format('MM/DD/YYYY');
                            }
                            if (column.id === 'amount' || column.id === 'balance') {
                              value = `$${roundTo(parseFloat(value || '0'), 2)}`;
                            }
                            const accountBalance = invoiceEdited.formattedEditedPaymentValue;

                            return (
                              <div
                                key={column.id}
                                style={{ flex: column.width / totalWidth }}
                                className="flex items-center w-full h-full justify-center">
                                {column.cellType === 'VIEW_TEXT' &&
                                  column.id !== 'select' &&
                                  column.id !== 'download' &&
                                  column.id !== 'actions' &&
                                  column.id !== 'signedBy' &&
                                  column.id !== 'open' &&
                                  column.id !== 'balance' &&
                                  column.id !== 'amount' &&
                                  column.id !== 'job' &&
                                  column.id !== 'isPastDueDate' && (
                                    <p className={'py-3 text-center text-xs ' + 'TEXT_PRIMARY-CLR'}>
                                      {value}
                                    </p>
                                  )}
                                {column.cellType === 'VIEW_TEXT' && column.id === 'payment' && (
                                  <div className="edit-invoice-input">
                                    <div className="text-sm">$</div>
                                    <input
                                      type={'number'}
                                      className={'w-full text-sm ' + 'TEXT_PRIMARY-CLR'}
                                      name={columnInvoiceNo}
                                      value={accountBalance}
                                      onChange={onChangeInput}
                                      onBlur={(e) => onBlurInput(e)}
                                    />
                                  </div>
                                )}
                                {column.cellType === 'VIEW_TEXT' && column.id === 'balance' && (
                                  <p className={'text-xs w-full py-3 ml-4 ' + 'TEXT_PRIMARY-CLR'}>
                                    {value}
                                  </p>
                                )}
                                {column.cellType === 'VIEW_TEXT' &&
                                  column.id === 'job' &&
                                  invoiceEdited.invoiceOriginal.quickBooksJob && (
                                    <div className={'text-sm w-full'}>
                                      <p className="font-bold text-xs text-center">
                                        {`${
                                          invoiceEdited.invoiceOriginal.quickBooksJob?.jobNumber
                                            ? `${invoiceEdited.invoiceOriginal.quickBooksJob?.jobNumber} - `
                                            : ''
                                        } ${invoiceEdited.invoiceOriginal.quickBooksJob?.jobName}`}
                                      </p>
                                      <p className="text-2xs text-center">
                                        {invoiceEdited.invoiceOriginal.quickBooksJob?.address ||
                                          invoiceEdited.invoiceOriginal.shipAddress?.address1}
                                      </p>
                                    </div>
                                  )}
                                {column.cellType === 'VIEW_TEXT' &&
                                  column.id !== 'select' &&
                                  column.id !== 'download' &&
                                  column.id !== 'actions' &&
                                  column.id !== 'isPastDueDate' &&
                                  column.id === 'signedBy' && (
                                    <div className="items-center justify-center ">
                                      {invoiceEdited.invoiceOriginal.dillInvoice &&
                                        invoiceEdited.invoiceOriginal.dillInvoice.isReceived && (
                                          <div className="items-center justify-center">
                                            {invoiceEdited.invoiceOriginal.dillInvoice
                                              .receivedBy && (
                                              <p
                                                className={
                                                  'text-xs font-bold text-center  ' +
                                                  'TEXT_PRIMARY-CLR '
                                                }>
                                                {
                                                  invoiceEdited.invoiceOriginal.dillInvoice
                                                    .receivedBy
                                                }
                                              </p>
                                            )}
                                            {invoiceEdited.invoiceOriginal.dillInvoice
                                              .receivedByTime && (
                                              <p
                                                className={
                                                  'text-xs text-center ' + 'TEXT_PRIMARY-CLR'
                                                }>
                                                {moment(
                                                  invoiceEdited.invoiceOriginal.dillInvoice
                                                    .receivedByTime
                                                ).format('MM/DD  LT')}
                                              </p>
                                            )}
                                          </div>
                                        )}
                                      {(!invoiceEdited.invoiceOriginal.dillInvoice ||
                                        (invoiceEdited.invoiceOriginal.dillInvoice &&
                                          !invoiceEdited.invoiceOriginal.dillInvoice
                                            .isReceived)) && (
                                        <p className={'text-sm  text-center ' + 'TEXT_PRIMARY-CLR'}>
                                          -
                                        </p>
                                      )}
                                    </div>
                                  )}
                                {column.cellType === 'VIEW_TEXT' &&
                                  column.id !== 'select' &&
                                  column.id !== 'download' &&
                                  column.id !== 'actions' &&
                                  column.id === 'isPastDueDate' && (
                                    <div
                                      style={{ width: '100px' }}
                                      className={
                                        value === 'true'
                                          ? 'rounded-lg text-sm SUCCESS_100-BG p-2 SUCCESS_600-CLR text-center'
                                          : 'rounded-lg text-sm WARNING_100-BG p-2 WARNING_600-CLR text-center'
                                      }>
                                      {value === 'true' ? 'Past Due' : 'Current Due'}
                                    </div>
                                  )}
                                {column.id === 'actions' && (
                                  <div className={''}>
                                    <Trash
                                      size={20}
                                      className="cursor-pointer"
                                      color={COLORS.ERROR_500}
                                      onClick={() => {
                                        onDeleteEditedInvoice(invoiceIndex);
                                      }}
                                    />
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                </div>
              )}
              {!isSelectPaymentMethod && isPartialPayment && !supplierChargeDetails && (
                <div className="mt-2 flex flex-col">
                  <div className="">
                    <AppDropDown
                      isRequired={true}
                      items={partialPaymentReasons}
                      value={partialPaymentReason}
                      onSelectChange={(data) => {
                        setPartialPaymentReason(data?.value ?? '');
                        setInvoiceListErrorMessage('');
                      }}
                      label="Select Short Payment Reason"
                      placeholder="Select a short payment reason"
                    />
                  </div>
                  <div className="">
                    <AppInputField
                      label="Short Payment Notes"
                      inputType="textarea"
                      value={partialPaymentNotes}
                      placeholder="Short payment notes"
                      onTextChange={(val) => {
                        setPartialPaymentNotes(val);
                      }}
                    />
                  </div>
                  <div className="">
                    <div className="my-2">
                      <div className="text-xs mb-0.5  whitespace-nowrap font-medium">
                        Short Payment Attachments
                      </div>
                      <AppFilePicker
                        onSelectFile={(
                          uploadedFiles: File[],
                          fileUrls?: { name: string; url: string }[]
                        ) => {
                          setPartialPaymentAttachmentsFiles(uploadedFiles);
                          if (fileUrls) {
                            setPartialPaymentAttachmentsUrls(fileUrls);
                          }
                        }}
                        fileUrls={partialPaymentAttachmentsUrls}
                      />
                    </div>
                  </div>
                </div>
              )}
              {isSelectPaymentMethod && totalPay - totalCreditsToUse + fees !== 0 && (
                <div className="flex flex-col h-full">
                  <div className="flex flex-row items-center justify-between ml-3">
                    {!supplierChargeDetails && (
                      <p className="text-sm font-bold ">Select a Payment Method</p>
                    )}
                    {supplierChargeDetails && (
                      <p className="text-sm font-bold ">Select a card to charge on file</p>
                    )}
                    <DillButton
                      label="New Method"
                      variant="ghost-blue"
                      icon={<Plus size={16} />}
                      onClick={() => {
                        // onChangeModalType('newPaymentMethod');
                        setIsAppAddPaymentMethodModalOpen(true);
                      }}
                    />
                  </div>
                  <ReactTooltip
                    id={'waiver-tool-tip'}
                    place="top"
                    variant="dark"
                    opacity={'100%'}
                    style={{
                      background: '#334155',
                      width: '300px',
                      zIndex: 99999,
                    }}>
                    <span>
                      Credit card fees waived because all invoices for this payment are not past due
                    </span>
                  </ReactTooltip>
                  <ReactTooltip
                    id={'discount-date-waiver-tool-tip'}
                    place="top"
                    variant="dark"
                    opacity={'100%'}
                    style={{
                      background: '#334155',
                      width: '300px',
                      zIndex: 99999,
                    }}>
                    <span>
                      Credit card fees waived because all invoices are within the discount date
                    </span>
                  </ReactTooltip>
                  <ReactTooltip
                    id={'day-of-month-waiver-tool-tip'}
                    place="top"
                    variant="dark"
                    opacity={'100%'}
                    style={{
                      background: '#334155',
                      width: '300px',
                      zIndex: 99999,
                    }}>
                    <span>
                      {`Credit card fees waived because all invoices are not past due and payment is being made on or before the ${getOrdinalSuffix(
                        effectiveWaiverConditions.currentDueWaiverDayOfMonthCutoff ?? 0
                      )} of the month, the deadline for this waiver`}
                    </span>
                  </ReactTooltip>
                  <div className="flex flex-col payment-options-container grow overflow-y-scroll">
                    {paymentMethods
                      .filter((pm) => {
                        if (
                          pm.provider === 'DWOLLA' &&
                          ((buyerForMainBuyer &&
                            buyerForMainBuyer.disabledPaymentTypes &&
                            buyerForMainBuyer.disabledPaymentTypes.includes('BANK')) ||
                            (supplierChargeDetails &&
                              supplierChargeDetails.customerDetails.buyer.disabledPaymentTypes &&
                              supplierChargeDetails.customerDetails.buyer.disabledPaymentTypes?.includes(
                                'BANK'
                              )))
                        ) {
                          return false;
                        }
                        if (pm.provider === 'DWOLLA') {
                          return true;
                        }
                        if (
                          pm.cardType &&
                          pm.cardType !== 'CREDIT' &&
                          selectedSupplierForBuyer?.isDebitCardDisabled
                        ) {
                          return false;
                        }
                        if (
                          pm.cardType &&
                          pm.cardType === 'CREDIT' &&
                          !supplierChargeDetails &&
                          buyerForMainBuyer &&
                          buyerForMainBuyer.disabledPaymentTypes &&
                          buyerForMainBuyer.disabledPaymentTypes.includes('CREDIT_CARD')
                        ) {
                          return false;
                        }
                        if (
                          pm.cardType &&
                          pm.cardType !== 'CREDIT' &&
                          !supplierChargeDetails &&
                          buyerForMainBuyer &&
                          buyerForMainBuyer.disabledPaymentTypes &&
                          buyerForMainBuyer.disabledPaymentTypes.includes('DEBIT_CARD')
                        ) {
                          return false;
                        }

                        if (
                          pm.type === 'CREDIT_CARD' &&
                          selectedSupplierForBuyer?.isOptoutCardPayments
                        ) {
                          return false;
                        }

                        if (
                          pm.provider === 'FISERV' &&
                          ((!supplierChargeDetails &&
                            selectedSupplierForBuyer?.cardPaymentProvider === 'FISERV') ||
                            (supplierChargeDetails &&
                              supplierChargeDetails.supplier?.cardPaymentProvider === 'FISERV'))
                        ) {
                          return true;
                        }
                        if (
                          pm.provider === 'STRIPE' &&
                          ((!supplierChargeDetails &&
                            (selectedSupplierForBuyer?.cardPaymentProvider === 'STRIPE' ||
                              !selectedSupplierForBuyer?.cardPaymentProvider)) ||
                            (supplierChargeDetails &&
                              (supplierChargeDetails.supplier?.cardPaymentProvider === 'STRIPE' ||
                                !supplierChargeDetails.supplier?.cardPaymentProvider)))
                        ) {
                          return true;
                        }
                        return false;
                      })
                      .map((paymentMethod, i) => {
                        const hasCreditCardFee =
                          (creditCardTakeRate && creditCardTakeRate > 0) ||
                          (!supplierChargeDetails &&
                            selectedSupplierForBuyer &&
                            getBuyerFiservFeeRate({
                              supplier: selectedSupplierForBuyer,
                              buyer: selectedSupplierForBuyer.buyer,
                            }) > 0) ||
                          (supplierChargeDetails && supplierChargeBuyerTakeRate > 0);
                        const isCreditCard = paymentMethod.cardType === 'CREDIT';

                        const canShowWaiverTooltip = Boolean(hasCreditCardFee) && isCreditCard;

                        const tooltipId =
                          canShowWaiverTooltip && waiverEarned.currentDueViaCutoff
                            ? 'day-of-month-waiver-tool-tip'
                            : canShowWaiverTooltip && waiverEarned.currentDue
                            ? 'waiver-tool-tip'
                            : canShowWaiverTooltip && waiverEarned.discountDate
                            ? 'discount-date-waiver-tool-tip'
                            : `payment-method-tooltip_${paymentMethod.id}`;

                        return (
                          <div
                            key={`${paymentMethod.id}_list`}
                            className={'w-full px-4 my-1 relative'}
                            data-tooltip-id={tooltipId}>
                            <div className="absolute right-5 top-2">
                              <Trash
                                size={16}
                                className="cursor-pointer"
                                color={COLORS.ERROR_500}
                                onClick={() => {
                                  handleDeletePaymentMethod(paymentMethod);
                                }}
                              />
                            </div>
                            <div
                              className={twMerge(
                                'flex flex-row rounded-xl  px-5 py-4 border justify-between items-center cursor-pointer ',
                                paymentMethod.id === selectedPaymentMethod
                                  ? 'PRIMARY_500-BORDER'
                                  : ' GREY_300-BORDER',
                                paymentMethod.status === 'MICRO_DEPOSITS_SENT' ||
                                  paymentMethod.status === 'MICRO_DEPOSITS_VERIFICATION_PENDING' ||
                                  paymentMethod.status === 'UNVERIFIED'
                                  ? 'bg-gray-100'
                                  : 'WHITE-BG'
                              )}
                              onClick={() => {
                                setIsAutoAdjustInvoicesToFitLimit(false);
                                if (!paymentMethod?.status) {
                                  onSelectPaymentMethod(paymentMethod.id);
                                  setInvoiceListErrorMessage('');
                                  setOpenDiscountDropDown(false);
                                  setOpenCreditMemoDropDown(false);
                                }
                                if (paymentMethod.cardType !== 'CREDIT' && supplierChargeDetails) {
                                  setSupplierChargeWhoPaysFee('SUPPLIER');
                                  onSupplierChargeWhoPaysFee('SUPPLIER');
                                  onSupplierChargeBuyerTakeRate(0);
                                }
                              }}>
                              <div className="flex flex-row ">
                                <div className="flex mr-2 items-center">
                                  <AppRadioButton
                                    isChecked={paymentMethod.id === selectedPaymentMethod}
                                  />
                                  <div className="payment-type-logo">
                                    {paymentMethod.type === 'BANK_ACCOUNT' ? (
                                      <Bank
                                        size={20}
                                        style={{ display: 'flex', alignSelf: 'center' }}
                                      />
                                    ) : (
                                      <CreditCard
                                        size={30}
                                        style={{ display: 'flex', alignSelf: 'center' }}
                                      />
                                    )}
                                  </div>
                                </div>
                                <div className="flex flex-col">
                                  {paymentMethod.type === 'BANK_ACCOUNT' ? (
                                    <div className="text-sm font-bold text-left">Bank Transfer</div>
                                  ) : paymentMethod.type === 'CREDIT_CARD' ? (
                                    <div className="text-sm font-bold text-left">
                                      {paymentMethod.cardType === 'CREDIT'
                                        ? 'Credit Card'
                                        : 'Debit / Other Card'}
                                    </div>
                                  ) : (
                                    <div className="text-sm font-bold text-left">
                                      Debit / Other Card
                                    </div>
                                  )}
                                  <p className="accountDetails">{`${paymentMethod.paymentMethodName}`}</p>
                                  {paymentMethod?.expiry && (
                                    <p className="accountDetails">{`Expires ${moment(
                                      paymentMethod.expiry,
                                      paymentMethod.expiry.length === 4 ? 'MMYY' : 'YYYYM'
                                    ).format('MM/YY')}`}</p>
                                  )}
                                  {paymentMethod?.user?.email && (
                                    <p className="text-2xs  text-left">
                                      {paymentMethod?.user?.email}
                                    </p>
                                  )}
                                </div>
                              </div>

                              {paymentMethod.type === 'CREDIT_CARD' &&
                              paymentMethod.cardType === 'CREDIT' &&
                              paymentMethod.provider === 'STRIPE' &&
                              creditCardTakeRate &&
                              creditCardTakeRate > 0 ? (
                                <div className="flex-end">
                                  <div className="fees-label">Fees</div>
                                  <div
                                    className={twMerge(
                                      'credit-card-fees',
                                      waiverEarned.any ? 'line-through' : ''
                                    )}>{`${creditCardTakeRate}%`}</div>
                                </div>
                              ) : paymentMethod.type === 'CREDIT_CARD' &&
                                paymentMethod.cardType === 'CREDIT' &&
                                paymentMethod.provider === 'FISERV' &&
                                selectedSupplierForBuyer &&
                                !supplierChargeDetails &&
                                getBuyerFiservFeeRate({
                                  supplier: selectedSupplierForBuyer,
                                  buyer: selectedSupplierForBuyer.buyer,
                                }) > 0 ? (
                                <div className="flex-end">
                                  <div className="fees-label">Fees</div>

                                  {!supplierChargeDetails && (
                                    <div
                                      className={twMerge(
                                        'credit-card-fees',
                                        waiverEarned.any ? 'line-through' : ''
                                      )}>{`${getBuyerFiservFeeRate({
                                      supplier: selectedSupplierForBuyer,
                                      buyer: selectedSupplierForBuyer.buyer,
                                    })}%`}</div>
                                  )}
                                </div>
                              ) : paymentMethod.type === 'CREDIT_CARD' &&
                                paymentMethod.cardType === 'CREDIT' &&
                                paymentMethod.provider === 'FISERV' &&
                                supplierChargeDetails &&
                                supplierChargeBuyerTakeRate > 0 ? (
                                <div className="flex-end">
                                  <div className="fees-label">Fees</div>

                                  <div
                                    className={twMerge(
                                      'credit-card-fees',
                                      waiverEarned.any ? 'line-through' : ''
                                    )}>{`${supplierChargeBuyerTakeRate}%`}</div>
                                </div>
                              ) : (
                                <></>
                              )}
                              {(paymentMethod.status === 'MICRO_DEPOSITS_SENT' ||
                                paymentMethod.status === 'MICRO_DEPOSITS_VERIFICATION_PENDING' ||
                                paymentMethod.status === 'UNVERIFIED') && (
                                <div className="flex-end">
                                  <div className="text-xs flex items-center mb-[2px]">
                                    <Warning size={18} color={COLORS.ERROR_500} className="mr-1" />
                                    Unverified
                                  </div>
                                  <div className="">
                                    <DillButton
                                      label="Verify Deposits"
                                      isDisabled={
                                        paymentMethod.status !==
                                        'MICRO_DEPOSITS_VERIFICATION_PENDING'
                                      }
                                      onClick={() => {
                                        setIsAppVerifyDwollaMicroTransactionsModalOpen(true);
                                        setSelectedPaymentMethodToVerifyMicroTransactions(
                                          paymentMethod
                                        );
                                      }}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                  {supplierChargeDetails && selectedPaymentMethodDetails && (
                    <div className="">
                      {selectedPaymentMethodDetails?.cardType !== 'CREDIT' &&
                        selectedPaymentMethodDetails?.type !== 'BANK_ACCOUNT' && (
                          <p className="text-xs px-4 font-bold mt-2">
                            Fee selection does not apply to debit cards, as surcharging the customer
                            on debit cards is not permitted according to federal law.
                          </p>
                        )}
                      {selectedPaymentMethodDetails?.cardType === 'CREDIT' && (
                        <div className="">
                          <div className="flex flex-col mt-4 gap-0">
                            <div
                              className="flex items-center cursor-pointer w-1/3"
                              onClick={() => {
                                setSupplierChargeWhoPaysFee('SUPPLIER');
                                onSupplierChargeWhoPaysFee('SUPPLIER');
                                onSupplierChargeBuyerTakeRate(0);
                              }}>
                              <AppRadioButton isChecked={supplierChargeWhoPaysFee === 'SUPPLIER'} />
                              <p className="text-xs ml-1 font-medium">Fees paid by supplier</p>
                            </div>

                            <div
                              className="flex items-center cursor-pointer "
                              onClick={() => {
                                setSupplierChargeWhoPaysFee('BOTH');
                                onSupplierChargeWhoPaysFee('BOTH');

                                if (currentSupplier?.cardPaymentProvider === 'FISERV') {
                                  onSupplierChargeBuyerTakeRate(
                                    getBuyerFiservFeeRate({
                                      buyer: supplierChargeDetails?.customerDetails?.buyer,
                                      supplier: currentSupplier,
                                    })
                                  );
                                } else {
                                  onSupplierChargeBuyerTakeRate(supplierTakeRate / 2);
                                }
                              }}>
                              <AppRadioButton
                                isChecked={
                                  supplierChargeWhoPaysFee === 'BOTH' ||
                                  supplierChargeWhoPaysFee === 'BUYER'
                                }
                              />
                              <p className="text-xs ml-1 font-medium">Fees paid by contractor</p>
                            </div>
                          </div>

                          {(supplierChargeWhoPaysFee === 'BOTH' ||
                            supplierChargeWhoPaysFee === 'BUYER') && (
                            <div className="flex flex-col mt-4 bg-slate-100 px-2 py-3 rounded-lg">
                              <p className="text-xs font-bold ">
                                Set how much of the transaction fees rate are paid by the contractor
                              </p>

                              <AppRangeSlider
                                key={`slider-${supplierChargeWhoPaysFee}-${supplierTakeRate}`}
                                min={0}
                                max={supplierTakeRate}
                                valueUnits="%"
                                valueLabel="Contractor fee rate"
                                otherValueLabel={
                                  currentSupplier?.cardPaymentProvider === 'FISERV'
                                    ? ''
                                    : 'Your fee rate'
                                }
                                value={supplierChargeBuyerTakeRate}
                                onChange={({ value }) => {
                                  onSupplierChargeBuyerTakeRate(value);
                                }}
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="payment-limit-container">
                    {weekLimitations && showBankLimit && (
                      <p className="text-xs font-thin PRIMARY_500-CLR ">{`Limit: ${formatMoney(
                        weekLimitations.weekLimit,
                        2
                      )}`}</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-col w-full ">
              {!isSelectPaymentMethod && (
                <div className="w-full px-4 my-4">
                  <div className="flex flex-col  mt-4 rounded-xl PRIMARY_50-BG px-5 py-4">
                    {isUseCredits && (
                      <div className="credit-display-container">
                        <div className="flex flex-row justify-between  mb-2 ">
                          <div className="amount-label-text">Amount</div>
                          <div className="amount-text">{`${formatMoney(totalPay, 2)}`}</div>
                        </div>
                        {selectedSupplierForBuyer?.integrationType !== 'INFOR_DIST_SX' &&
                        selectedSupplierForBuyer?.integrationType !== 'DISTRIBUTION_ONE' &&
                        selectedSupplierForBuyer?.integrationType !== 'SAGE_100' &&
                        selectedSupplierForBuyer?.integrationType !== 'P21' &&
                        selectedSupplierForBuyer?.integrationType !== 'MANUAL_UPLOAD' &&
                        selectedSupplierForBuyer?.integrationType !== 'ABLE_MANUAL_UPLOAD' &&
                        selectedSupplierForBuyer?.integrationType !== 'P21_MANUAL_UPLOAD' &&
                        selectedSupplierForBuyer?.integrationType !== 'DDI_MANUAL_UPLOAD' &&
                        selectedSupplierForBuyer?.integrationType !== 'ECLIPSE_MANUAL_UPLOAD' &&
                        selectedSupplierForBuyer?.integrationType !== 'ECLIPSE' &&
                        selectedSupplierForBuyer?.integrationType !== 'MARKS_MANUAL_UPLOAD' &&
                        selectedSupplierForBuyer?.integrationType !== 'EAGLE_MANUAL_UPLOAD' ? (
                          <div className="flex flex-row justify-between  mb-2 ">
                            <div className="amount-label-text">Credits</div>
                            <div className="amount-text">{`-${formatMoney(
                              totalCreditsToUse,
                              2
                            )}`}</div>
                          </div>
                        ) : (
                          renderCreditDropDown()
                        )}
                      </div>
                    )}
                    <div className="flex flex-row justify-between   ">
                      <p className="amount-text">
                        Total {`${!supplierChargeDetails ? 'Payment' : 'Charge'}`}
                      </p>
                      <div className="flex flex-col items-end">
                        <p className="totalText">{`${formatMoney(
                          totalPay - (totalCreditsToUse - Math.abs(creditDiscountTotal)),
                          2
                        )}`}</p>
                      </div>
                    </div>
                  </div>
                  {!selectedSupplierForBuyer?.integrationType &&
                    selectedSupplierForBuyer?.invoices?.creditMemosDetails.totalRemainingCredit !==
                      undefined &&
                    selectedSupplierForBuyer?.invoices?.creditMemosDetails.totalRemainingCredit >
                      0 && (
                      <div
                        className="mt-3 flex relative items-center w-1/3"
                        data-tooltip-id={'credits_soon'}>
                        {selectedSupplierForBuyer.integrationType === 'CODAT_NETSUITE' && (
                          //  && totalPay - creditAmount === 0
                          <div className="absolute bg-white opacity-50 h-full w-full"></div>
                        )}
                        <div className="absolute bg-white opacity-50 h-full w-full"></div>
                        <ReactTooltip
                          id={'credits_soon'}
                          place="right"
                          variant="dark"
                          opacity={'100%'}
                          style={{
                            background: '#222',
                            width: '200px',
                            zIndex: 99999,
                          }}>
                          <span>Applying credits will be available on the Dill portal soon</span>
                        </ReactTooltip>
                        <DillCheckbox
                          isChecked={isUseCredits}
                          onChange={() => {
                            onUseCredits(!isUseCredits);
                            if (
                              !isUseCredits &&
                              selectedSupplierForBuyer?.invoices?.creditMemosDetails
                                .totalRemainingCredit
                            ) {
                              if (
                                selectedSupplierForBuyer?.invoices?.creditMemosDetails
                                  .totalRemainingCredit > totalPay
                              ) {
                                onSetCreditToUse(totalPay);
                              } else {
                                onSetCreditToUse(
                                  selectedSupplierForBuyer?.invoices?.creditMemosDetails
                                    .totalRemainingCredit
                                );
                              }
                            } else {
                              onSetCreditToUse(0);
                            }
                          }}
                          className="h-5 w-5 rounded-md"
                        />
                        <div className={twMerge('ml-2 use-credit-text ')}>
                          Use <span>{`${formatMoney(creditAmount, 2)}`}</span>Credits
                        </div>
                      </div>
                    )}
                  {/* {selectedSupplier &&
                    selectedSupplier.integrationType === 'CODAT_NETSUITE' &&
                    totalPay - creditAmount === 0 && (
                      <p className="text-2xs text-red-600">
                        You can not make a full credit transaction
                      </p>
                    )} */}
                </div>
              )}
              {isSelectPaymentMethod && (
                <div className="w-full px-4">
                  <div className="flex flex-col justify-between  mt-4 rounded-xl PRIMARY_50-BG px-5 py-4">
                    <div>
                      <div className="flex flex-row justify-between  mb-2 ">
                        <div className="amount-label-text">Amount</div>
                        <div className="amount-text">{`${formatMoney(totalPay, 2)}`}</div>
                      </div>
                      {(discountPayment > 0 || Math.abs(creditDiscountTotal) > 0) &&
                        renderDiscountDropDown()}
                      {isUseCredits && (
                        <div>
                          {selectedSupplierForBuyer?.integrationType !== 'INFOR_DIST_SX' &&
                          selectedSupplierForBuyer?.integrationType !== 'DISTRIBUTION_ONE' &&
                          selectedSupplierForBuyer?.integrationType !== 'SAGE_100' &&
                          selectedSupplierForBuyer?.integrationType !== 'P21' &&
                          selectedSupplierForBuyer?.integrationType !== 'MANUAL_UPLOAD' &&
                          selectedSupplierForBuyer?.integrationType !== 'ABLE_MANUAL_UPLOAD' &&
                          selectedSupplierForBuyer?.integrationType !== 'P21_MANUAL_UPLOAD' &&
                          selectedSupplierForBuyer?.integrationType !== 'DDI_MANUAL_UPLOAD' &&
                          selectedSupplierForBuyer?.integrationType !== 'ECLIPSE_MANUAL_UPLOAD' &&
                          selectedSupplierForBuyer?.integrationType !== 'MARKS_MANUAL_UPLOAD' &&
                          selectedSupplierForBuyer?.integrationType !== 'EAGLE_MANUAL_UPLOAD' ? (
                            <div className="flex flex-row justify-between  mb-2 ">
                              <div className="amount-label-text">Credits</div>
                              <div className="amount-text">{`-${formatMoney(
                                totalCreditsToUse,
                                2
                              )}`}</div>
                            </div>
                          ) : (
                            renderCreditDropDown()
                          )}
                        </div>
                      )}
                    </div>
                    {fees !== 0 && (
                      <div className="flex flex-row justify-between w-full pb-2">
                        <div className="detailText w-1/5">Fees</div>
                        <div className="amount-text">{`+${formatMoney(fees, 2)}`}</div>
                      </div>
                    )}
                    {(waivedFees ?? 0) > 0 && (
                      <div className="flex flex-row justify-between w-full pb-2">
                        <div className="detailText w-1/5 line-through">Waived fees</div>
                        <div className="amount-text line-through">{`+${formatMoney(
                          waivedFees || 0,
                          2
                        )}`}</div>
                      </div>
                    )}
                    <div className="flex flex-row justify-between w-full pt-2  border-t">
                      <div className="amount-text">
                        Total {!supplierChargeDetails ? 'Payment' : 'Charge'}
                      </div>
                      <div className="totalText">{`${formatMoney(
                        totalPay -
                          (totalCreditsToUse - Math.abs(creditDiscountTotal)) -
                          discountPayment +
                          fees,
                        2
                      )}`}</div>
                    </div>
                  </div>
                  {/* Scheduling is an opt-in on top of the payment, not a fork in it: paying now is
                      the default and needs no control of its own. So this is one toggle — add a date,
                      or take it away again. */}
                  {canSchedulePayment && (
                    <div className="mt-3">
                      {paymentTiming === 'NOW' ? (
                        <button
                          type="button"
                          onClick={() => onPaymentTimingChange('SCHEDULED')}
                          className="flex w-full items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-3 text-left transition-colors hover:border-blue-400 hover:bg-blue-50">
                          <Clock size={20} className="text-blue-500 shrink-0" />
                          <span className="text-sm font-medium text-gray-900">
                            Schedule for later
                          </span>
                          <span className="ml-auto text-xs text-gray-400">Optional</span>
                        </button>
                      ) : (
                        <div className="rounded-lg border border-blue-500 bg-blue-50 px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Clock size={20} weight="fill" className="text-blue-500 shrink-0" />
                            <span className="text-sm font-medium text-gray-900">
                              Scheduled for later
                            </span>
                            <button
                              type="button"
                              onClick={() => onPaymentTimingChange('NOW')}
                              className="ml-auto text-xs font-medium text-gray-500 underline hover:text-gray-800">
                              Undo
                            </button>
                          </div>
                          <div className="mt-3">
                            <AppDatePicker
                              selectedDate={scheduledDate}
                              onDateSelected={(date) => onScheduledDateChange(date ?? null)}
                              minumumDate={earliestScheduledDate}
                              dateFormat="MM/dd/yyyy"
                              label="Payment date"
                            />
                            <p className="text-xs text-gray-500 mt-1 m-0">
                              We&apos;ll send this payment on the morning of that date. You can
                              change it or cancel any time before then.
                            </p>
                          </div>
                        </div>
                      )}

                      {scheduleErrorMessage && (
                        <p className="text-xs text-red-500 m-0 mt-1 px-1">{scheduleErrorMessage}</p>
                      )}
                    </div>
                  )}
                  {!canSchedulePayment && scheduleUnavailableReason && (
                    <div className="mt-3 flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                      <Clock size={20} className="text-gray-400 shrink-0" />
                      <span className="text-xs text-gray-500">{scheduleUnavailableReason}</span>
                    </div>
                  )}
                  {!supplierChargeDetails && width > 724 && (
                    <div className="create-release-info ">
                      <div className="mr-2">
                        <LightbulbFilament color={COLORS.INFO_900} size={24} />
                      </div>
                      <div className="create-release-info-text ">
                        We have a special feature that <span>auto-generates</span> the lien waiver
                        request for you when you pay through Dill! If you would like to add more
                        templates or supplier contacts, please go to the Lien Waivers tab
                      </div>
                    </div>
                  )}
                  {!supplierChargeDetails && width > 724 && (
                    <div className="my-3 flex items-center">
                      <DillCheckbox
                        isChecked={sendRealese}
                        onChange={() => onAutoGenerateRelease()}
                      />
                      <div className="ml-2 text-sm cursor-pointer" onClick={onAutoGenerateRelease}>
                        Auto-generate lien waiver request (Optional)
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          {isSelectPaymentMethod && sendRealese && (
            <div className="payment-invoice-table-modal-right-content px-3 border-l ">
              <AppCreateRealease
                selectedRelease={selectedRelease}
                paymentAmount={totalPay}
                onCreateRelease={handleCreateRelease}
                groupedInvoiceReleases={groupedInvoiceReleases}
                releaseErrors={releaseErrors}
              />
            </div>
          )}
        </div>
        <div>
          <hr className="h-px my-1 bg-gray-200 border-0 dark:bg-gray-200"></hr>
          <div className="flex items-center justify-center py-1 px-3 w-full">
            {((Object.keys(releaseErrors).length > 0 && sendRealese) ||
              selectedPaymentMethod == '') &&
              isSelectPaymentMethod && (
                <div className="create-release-error">
                  <div className="mr-2">
                    <Warning color={COLORS.ERROR_500} size={24} />
                  </div>
                  <div>
                    {selectedPaymentMethod == '' && (
                      <div className="create-release-error-info">
                        Payment Method is not selected
                      </div>
                    )}
                    {Object.keys(releaseErrors).length > 0 && sendRealese && (
                      <div className="create-release-error-info">
                        Please review for any missing fields in your lien waiver requests
                      </div>
                    )}
                  </div>
                </div>
              )}
          </div>
          {invoiceListErrorMessage && (
            <div className="px-4 w-full md:w-[600px]">
              {!isAutoAdjustInvoicesToFitLimit && (
                <p className="text-xs  text-center ERROR_500-CLR px-3">{invoiceListErrorMessage}</p>
              )}
              {isAutoAdjustInvoicesToFitLimit && (
                <div className="WARNING_300-BG rounded-lg flex flex-col justify-center mt-2 py-5 px-3">
                  <div className="flex">
                    <Warning size={28} className="w-2/12 WARNING_900-CLR" />
                    <div className="">
                      <p className="text-sm font-medium WARNING_900-CLR ml-2 ">
                        {invoiceListErrorMessage}
                      </p>
                      <div className="mt-3">
                        <DillButton
                          variant="secondary"
                          label="Auto Adjust"
                          onClick={() => {
                            setIsAutoAdjustInvoicesToFitLimit(false);
                            onAutoAdjustInvoicesToFitLimit();
                            setInvoiceListErrorMessage('');
                            setReleaseData([]);
                            const otherReasonCheck = partialPaymentReasons.find(
                              (reason) => reason.value.toLowerCase() === 'other'
                            );
                            let reason = '';
                            if (otherReasonCheck) {
                              reason = otherReasonCheck?.value;
                            } else if (partialPaymentReasons.length > 0) {
                              reason = partialPaymentReasons[0]?.value;
                            } else {
                              reason = 'Other';
                            }
                            setPartialPaymentReason(reason);
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          {totalPay - (totalCreditsToUse - Math.abs(creditDiscountTotal)) - discountPayment <
            0.5 && (
            <div className="text-center px-2 text-xs ERROR_500-CLR">
              The amount to be paid should be greater than 0.5. Please reselect the credits.
            </div>
          )}
          <div className="flex flex-row-reverse self-end px-4 py-2 w-full gap-2">
            <DillButton
              label={`${
                // Scheduling swaps the verb and nothing else — the amount stays on the button,
                // because that is where the buyer reads what they are committing to. 'Next' is the
                // invoice-list step and promises nothing about timing, so it is left alone.
                totalCreditsToUse === totalPay &&
                Object.values(selectedCreditMemos).flat().length === 0
                  ? isSchedulingPayment
                    ? 'Schedule Payment'
                    : `${!supplierChargeDetails ? 'Pay Now' : 'Charge Now'}`
                  : isSelectPaymentMethod
                  ? `${
                      isSchedulingPayment
                        ? 'Schedule'
                        : `${!supplierChargeDetails ? 'Pay' : 'Charge'}`
                    } ${formatMoney(
                      totalPay -
                        (totalCreditsToUse - Math.abs(creditDiscountTotal)) -
                        discountPayment +
                        fees,
                      2
                    )}`
                  : 'Next'
              }`}
              width={isSchedulingPayment ? 170 : 130}
              onClick={handlePayment}
              isDisabled={
                totalPay - discountPayment < 0.5 ||
                (totalPay - (totalCreditsToUse - Math.abs(creditDiscountTotal)) - discountPayment <
                  0.5 &&
                  Object.values(selectedCreditMemos).flat().length > 0)
              }
            />
            <DillButton
              label={isSelectPaymentMethod ? 'Back' : 'Close'}
              variant="secondary"
              onClick={() => {
                if (isSelectPaymentMethod) {
                  onSelectPaymentMethod('');
                  onToggleIsPaymentMethod(false);
                  setInvoiceListErrorMessage('');
                  setReleaseData([]);
                  setOpenCreditMemoDropDown(false);
                } else {
                  handleCloseModal();
                }
              }}
            />
          </div>
        </div>
        {paymentMethods
          .filter(
            (pymt) =>
              pymt.status === 'MICRO_DEPOSITS_SENT' ||
              pymt.status === 'MICRO_DEPOSITS_VERIFICATION_PENDING' ||
              pymt.status === 'UNVERIFIED'
          )
          .map((paymentMethod) => {
            return (
              <ReactTooltip
                id={`payment-method-tooltip_${paymentMethod.id}`}
                place="top"
                key={`${paymentMethod.id}ii`}
                variant="dark"
                opacity={'100%'}
                style={{
                  display: 'flex',
                  background: '#334155',
                  width: '350px',
                  zIndex: '70',
                  borderRadius: '8px',
                  alignItems: 'center',
                  justifyItems: 'center',
                  fontWeight: '600',
                }}>
                {paymentMethod.status === 'MICRO_DEPOSITS_VERIFICATION_PENDING' ? (
                  <span className="text-xs">
                    Two micro-deposits have arrived in your account. Please check for these deposits
                    in your account and verify them in order to use this bank method. This is a
                    one-time setup for this bank account.
                  </span>
                ) : (
                  <span className="text-xs">
                    Two micro-deposits were sent to this bank account. They should arrive 1-2
                    business days from the day it was added. Once you see these deposits in your
                    account, please verify them in order to use this bank method. This is a one-time
                    setup for this bank account.
                  </span>
                )}
              </ReactTooltip>
            );
          })}
      </div>
    </div>
  );
};
